if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activeIndex = 0;
        this.pointerStart = null;
        this.controlsReady = false;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
      }

      show(opener) {
        super.show(opener);
        this.ensureControls();
        this.showActiveMedia();
      }

      hide() {
        this.pauseInactiveMedia(null);
        super.hide();
      }

      getMediaItems() {
        const content = this.querySelector('.product-media-modal__content');
        return content ? Array.from(content.children).filter((item) => item.hasAttribute('data-media-id')) : [];
      }

      ensureControls() {
        if (this.controlsReady) return;

        const dialog = this.querySelector('.product-media-modal__dialog');
        const content = this.querySelector('.product-media-modal__content');
        const items = this.getMediaItems();
        if (!dialog || !content || items.length === 0) return;

        this.previousButton = this.createArrow('previous', '‹', 'Show previous product image');
        this.nextButton = this.createArrow('next', '›', 'Show next product image');
        this.previousButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.setActive(this.activeIndex - 1);
        });
        this.nextButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.setActive(this.activeIndex + 1);
        });
        [this.previousButton, this.nextButton].forEach((button) => {
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.tray = document.createElement('div');
        this.tray.className = 'lennie-media-modal__tray';

        this.thumbnailList = document.createElement('div');
        this.thumbnailList.className = 'lennie-media-modal__thumbnails';
        this.thumbnailList.setAttribute('role', 'tablist');
        this.thumbnailList.setAttribute('aria-label', 'Choose a product image');

        this.counter = document.createElement('span');
        this.counter.className = 'lennie-media-modal__counter';
        this.counter.setAttribute('aria-live', 'polite');

        items.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'lennie-media-modal__thumbnail';
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-label', `Show product image ${index + 1} of ${items.length}`);

          const sourceImage = item.matches('img') ? item : item.querySelector('img');
          if (sourceImage) {
            const thumbnail = document.createElement('img');
            thumbnail.src = sourceImage.currentSrc || sourceImage.src;
            thumbnail.alt = sourceImage.alt || '';
            thumbnail.loading = 'lazy';
            thumbnail.width = 96;
            thumbnail.height = 96;
            button.appendChild(thumbnail);
          } else {
            button.textContent = String(index + 1);
          }

          button.addEventListener('click', () => this.setActive(index, { focusThumbnail: true }));
          this.thumbnailList.appendChild(button);
        });

        this.tray.append(this.thumbnailList, this.counter);
        // Dawn closes a media modal on mouse pointerup outside the media itself.
        // Keep clicks on our navigation controls inside the open gallery.
        this.tray.addEventListener('pointerup', (event) => event.stopPropagation());
        this.tray.addEventListener('click', (event) => event.stopPropagation());
        dialog.append(this.previousButton, this.nextButton, this.tray);

        dialog.addEventListener('keydown', this.onKeyDown);
        content.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        content.addEventListener('pointerup', this.onPointerUp, { passive: true });
        content.addEventListener('pointercancel', () => {
          this.pointerStart = null;
        });

        const hasMultipleItems = items.length > 1;
        this.previousButton.hidden = !hasMultipleItems;
        this.nextButton.hidden = !hasMultipleItems;
        this.tray.hidden = !hasMultipleItems;
        this.controlsReady = true;
      }

      createArrow(direction, symbol, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `lennie-media-modal__arrow lennie-media-modal__arrow--${direction}`;
        button.setAttribute('aria-label', label);
        button.innerHTML = `<span aria-hidden="true">${symbol}</span>`;
        return button;
      }

      showActiveMedia() {
        const items = this.getMediaItems();
        if (items.length === 0) return;

        const requestedId = this.openedBy && this.openedBy.getAttribute('data-media-id');
        const requestedIndex = items.findIndex((item) => item.getAttribute('data-media-id') === requestedId);
        this.setActive(requestedIndex >= 0 ? requestedIndex : 0);
      }

      setActive(index, { focusThumbnail = false } = {}) {
        const items = this.getMediaItems();
        if (items.length === 0) return;

        this.activeIndex = (index + items.length) % items.length;
        items.forEach((item, itemIndex) => {
          const isActive = itemIndex === this.activeIndex;
          item.classList.toggle('active', isActive);
          item.setAttribute('aria-hidden', String(!isActive));
        });

        const thumbnails = this.thumbnailList ? Array.from(this.thumbnailList.children) : [];
        thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const isActive = thumbnailIndex === this.activeIndex;
          thumbnail.setAttribute('aria-current', String(isActive));
          thumbnail.setAttribute('aria-selected', String(isActive));
          thumbnail.tabIndex = isActive ? 0 : -1;
        });

        const activeThumbnail = thumbnails[this.activeIndex];
        if (activeThumbnail) {
          activeThumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          if (focusThumbnail) activeThumbnail.focus({ preventScroll: true });
        }

        if (this.counter) this.counter.textContent = `${this.activeIndex + 1} / ${items.length}`;

        const activeItem = items[this.activeIndex];
        this.pauseInactiveMedia(activeItem);
        if (activeItem && activeItem.nodeName === 'DEFERRED-MEDIA' && typeof activeItem.loadContent === 'function') {
          activeItem.loadContent();
        }
      }

      pauseInactiveMedia(activeItem) {
        this.getMediaItems().forEach((item) => {
          if (item === activeItem) return;
          item.querySelectorAll('video, audio').forEach((media) => media.pause());
        });
      }

      onKeyDown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.setActive(this.activeIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.setActive(this.activeIndex + 1);
        }
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' || event.button !== 0) return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      }

      onPointerUp(event) {
        if (!this.pointerStart || event.pointerType === 'mouse') return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;

        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.setActive(this.activeIndex + (deltaX < 0 ? 1 : -1));
      }
    },
  );
}
